import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Warehouse, CreateWarehouseData } from '../../types/warehouse';
import Toggle from '../common/Toggle';
import FormInput from '../common/FormInput';

interface WarehouseFormProps {
  warehouse: Warehouse | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateWarehouseData) => void;
}

const WarehouseForm: React.FC<WarehouseFormProps> = ({ warehouse, isOpen, onClose, onSave }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<CreateWarehouseData>({
    name: '',
    code: '',
    location: '',
    description: '',
    isActive: true,
    isDefault: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Adatok betöltése szerkesztés esetén
  useEffect(() => {
    if (warehouse) {
      setFormData({
        name: warehouse.name,
        code: warehouse.code,
        location: warehouse.location || '',
        description: warehouse.description || '',
        isActive: warehouse.isActive,
        isDefault: warehouse.isDefault || false
      });
    } else {
      // Új raktár esetén üres form
      setFormData({
        name: '',
        code: '',
        location: '',
        description: '',
        isActive: true,
        isDefault: false
      });
    }
  }, [warehouse]);

  // Form mezők változásának kezelése
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Hiba törlése, ha a mező ki lett töltve
    if (errors[name] && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Toggle komponensek kezelése
  const handleToggleChange = (name: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Kód mező automatikus kitöltése név alapján
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      name: value,
      // Csak akkor frissítjük a kódot automatikusan, ha még nem változtatta meg a felhasználó
      code: prev.code === '' || prev.code === prev.name.toLowerCase().replace(/\s+/g, '_')
        ? value.toLowerCase().replace(/\s+/g, '_')
        : prev.code
    }));
    
    // Hiba törlése, ha a mező ki lett töltve
    if (errors.name && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
  };

  // Form elküldése
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validáció
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }
    
    if (!formData.code.trim()) {
      newErrors.code = t('validation.required');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Adatok elküldése
    onSave(formData);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-10 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70" />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-xl max-w-md w-full mx-auto shadow-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {warehouse ? t('warehouse.edit') : t('warehouse.create')}
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 p-1"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput
              id="name"
              name="name"
              label={t('warehouse.fields.name')}
              value={formData.name}
              onChange={handleNameChange}
              error={errors.name}
              required
            />
            
            <FormInput
              id="code"
              name="code"
              label={t('warehouse.fields.code')}
              value={formData.code}
              onChange={handleChange}
              error={errors.code}
              required
            />
            
            <FormInput
              id="location"
              name="location"
              label={t('warehouse.fields.location')}
              value={formData.location}
              onChange={handleChange}
              error={errors.location}
            />
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('warehouse.fields.description')}
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            
            <div className="flex flex-col space-y-3">
              <Toggle
                id="isActive"
                label={t('warehouse.fields.isActive')}
                checked={formData.isActive ?? true}
                onChange={(checked: boolean) => handleToggleChange('isActive', checked)}
              />
              
              <Toggle
                id="isDefault"
                label={t('warehouse.fields.isDefault')}
                checked={formData.isDefault ?? false}
                onChange={(checked: boolean) => handleToggleChange('isDefault', checked)}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
              >
                {t('common.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
};

export default WarehouseForm; 